// docs/62 — emote pictures in the DOM. `EmoteSprite` is one emote at one frame, from the atlas,
// scaled by whole pixels. `EmoteAnchor` sits inside a hero's sprite (any positioned box): it
// registers the heroes it stands for as on screen and pops the bubble above them while one of them
// is emoting, animated through the sheet's frames (the wheel's still under reduced motion).
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { EMOTE_ATLAS, EMOTE_FRAMES, EMOTE_PX, EMOTES, PET_EMOTES, frameAt } from '../../core/emote/catalogue';
import { useEmoteHeroes, useEmotes, type ActiveEmote, type EmoteHero } from '../../core/emote/store';
import { useReducedMotion } from '../components/Motion';
import './emote.css';

export function EmoteSprite({ emote, frame, scale = 2, className = '' }: { emote: number; frame?: number; scale?: number; className?: string }) {
  const def = EMOTES[emote];
  const f = frame ?? def?.frame ?? 0;
  const px = EMOTE_PX * scale;
  return (
    <span className={`fk-emote ${className}`} role="img" aria-label={def?.name ?? 'emote'} data-emote={def?.key}
      style={{ width: px, height: px, backgroundImage: `url(${EMOTE_ATLAS})`, backgroundSize: `${EMOTE_FRAMES * px}px ${EMOTES.length * px}px`, backgroundPosition: `-${f * px}px -${emote * px}px` }} />
  );
}

function Playing({ a, scale }: { a: ActiveEmote; scale: number }) {
  const still = useReducedMotion();
  const [frame, setFrame] = useState(() => (still ? EMOTES[a.emote]?.frame : frameAt(a.at)) ?? 15);
  useEffect(() => {
    if (still) return;
    let raf = 0;
    const tick = () => {
      const f = frameAt(a.at);
      if (f === undefined) return;
      setFrame(f);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [a.at, a.seq, still]);
  return <span className="fk-emote-bubble" data-testid="emote-bubble" data-emote={EMOTES[a.emote]?.key}><EmoteSprite emote={a.emote} frame={frame} scale={scale} /></span>;
}

/** The newest emote playing for any of these heroes. */
function useLatest(heroes: EmoteHero[]): ActiveEmote | undefined {
  return useEmotes((s) => {
    let best: ActiveEmote | undefined;
    for (const h of heroes) {
      const a = s.active[h.id.toString()];
      if (a && (!best || a.seq > best.seq)) best = a;
    }
    return best;
  });
}

/**
 * Put inside a hero's box (position: relative): the bubble floats over its top edge. `heroes` is
 * usually one; a band's "+N" chip stands for the heroes it hides.
 */
export function EmoteAnchor({ heroes, scale = 2 }: { heroes: EmoteHero[]; scale?: number }) {
  useEmoteHeroes(heroes);
  const a = useLatest(heroes);
  if (!a) return null;
  return <Playing key={a.seq} a={a} scale={scale} />;
}

/**
 * A ranger's companion's sprite (docs/59): registers it as on screen (so its pet emotes sound
 * here and its owner's wheel can pick it) and wiggles it while it emotes. `lean` is the side its
 * ranger stands on, for Comfort.
 */
export function PetWiggle({ pet, lean = -1, children }: { pet: EmoteHero; lean?: -1 | 1; children: ReactNode }) {
  useEmoteHeroes([pet]);
  const a = useEmotes((s) => s.pets[pet.id.toString()]);
  const mood = a ? PET_EMOTES[a.emote]?.key : undefined;
  return (
    <span key={a?.seq ?? 0} className={`fk-pet${mood ? ` fk-pet--${mood}` : ''}`} data-testid="pet-wiggle" data-pet-emote={mood}
      style={{ '--lean': lean } as CSSProperties}>{children}</span>
  );
}
